import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { confirmNewProduct } from './productsSlice';
import { extractBaseSku, findAvailableSku } from '../../utils/skuUtils';

// Интерфейс для товара в New Wave
export interface NewWaveProduct {
  id: string;
  name: string | null;
  subject_id: number | null;
  url: string | null;
  thumb_url: string | null;
  thumb_middle_url: string | null;
  // Данные из MPStats API
  balance: number | null;
  comments: number | null;
  rating: number | null;
  final_price: number | null;
  final_price_max: number | null;
  final_price_min: number | null;
  final_price_average: number | null;
  final_price_median: number | null;
  sales: number | null;
  sales_per_day_average: number | null;
  days_in_stock: number | null;
  days_with_sales: number | null;
  average_if_in_stock: number | null;
  commission_fbo: number | null;
  commission_fbs: number | null;
  search_words_count: number | null;
  search_position_avg: number | null;
  search_visibility: number | null;
  warehouses_count: number | null;
  size_count: number | null;
  size_count_in_stock: number | null;
  // Графики
  graph: number[];
  stocks_graph: number[];
  price_graph: number[];
  product_visibility_graph: number[];
  category_graph: number[];
  category_position_graph: number[];
  search_position_graph: number[];
  warehouses_count_graph: number[];
  size_count_in_stock_graph: number[];
  // Дополнительные поля для редактирования (как в NewProducts)
  article?: string;
  requiredPrice?: number;
  certificate?: string;
  orderQuantity?: string;
  packageLength?: number;
  packageWidth?: number;
  packageHeight?: number;
  purchasePercent?: number;
  calculatedCostPrice?: number;
  calculatedYuanPrice?: number;
  // Статус
  isSaved: boolean;
  isSaving: boolean;
  new_product?: boolean;
  target_price_cny?: number | null;
  validationErrors?: {
    id?: boolean;
    subject_id?: boolean;
    name?: boolean;
    url?: boolean;
    thumb_url?: boolean;
    orderQuantity?: boolean;
    packageLength?: boolean;
    packageWidth?: boolean;
    packageHeight?: boolean;
    requiredPrice?: boolean;
    manualYuanPrice?: boolean;
    purchasePercent?: boolean;
  };
}

export interface NewWavePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface NewWaveState {
  products: NewWaveProduct[];
  pagination: NewWavePagination;
  loading: boolean;
  error: string | null;
  fetchingSku: boolean;
  fetchingSkuError: string | null;
  listRequestId: string | null;
}

const initialState: NewWaveState = {
  products: [],
  pagination: { page: 1, limit: 50, total: 0, totalPages: 1 },
  loading: false,
  error: null,
  fetchingSku: false,
  fetchingSkuError: null,
  listRequestId: null,
};

// Асинхронное действие для получения данных по SKU
export const fetchProductBySku = createAsyncThunk(
  'newWave/fetchProductBySku',
  async (sku: string, { rejectWithValue }) => {
    try {
      // 1. Получаем данные товара из MPStats API
      const response = await api.getProductBySku(sku);
      const productData = response.data.data || response.data;
      
      // 2. Проверяем доступность SKU в БД
      const originalSku = productData.id !== null && productData.id !== undefined 
        ? String(productData.id) 
        : sku;
      
      const checkResponse = await api.checkSkuAvailability(originalSku);
      const availableSku = checkResponse.data.data?.availableSku || originalSku;
      
      // 3. Возвращаем данные товара с доступным SKU
      return {
        ...productData,
        id: availableSku, // Используем SKU, проверенный в БД
        _originalSku: originalSku // Сохраняем оригинальный для справки
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch product by SKU');
    }
  }
);

// Асинхронное действие для загрузки товаров с new_wave = true (пагинация по 50)
export const fetchNewWaveProducts = createAsyncThunk(
  'newWave/fetchNewWaveProducts',
  async ({ page = 1, limit = 50 }: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const res = await api.getNewWaveProducts(page, limit);
      return {
        data: res.data || [],
        total: res.total ?? 0,
        page: res.page ?? page,
        limit: res.limit ?? limit,
        totalPages: res.totalPages ?? 1
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch new wave products');
    }
  }
);

// Асинхронное действие для сохранения товара
export const saveNewWaveProduct = createAsyncThunk(
  'newWave/saveProduct',
  async ({ productId, productData, imageFile }: { 
    productId: string; 
    productData: Partial<NewWaveProduct>; 
    imageFile?: File;
  }, { rejectWithValue }) => {
    try {
      const response = await api.saveNewWaveProduct(productId, productData, imageFile);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { error: error.message || 'Failed to save product' });
    }
  }
);

const newWaveSlice = createSlice({
  name: 'newWave',
  initialState,
  reducers: {
    // Добавление товара в локальный список
    addProduct: (state, action: PayloadAction<NewWaveProduct>) => {
      // Проверяем, нет ли уже товара с таким ID
      const existingIndex = state.products.findIndex(p => p.id === action.payload.id);
      if (existingIndex === -1) {
        state.products.push(action.payload);
      } else {
        // Обновляем существующий товар
        state.products[existingIndex] = action.payload;
      }
    },
    // Обновление данных товара
    updateProduct: (state, action: PayloadAction<{ productId: string; updates: Partial<NewWaveProduct> }>) => {
      const index = state.products.findIndex(p => p.id === action.payload.productId);
      if (index !== -1) {
        state.products[index] = {
          ...state.products[index],
          ...action.payload.updates
        };
      }
    },
    // Удаление товара из списка
    removeProduct: (state, action: PayloadAction<string>) => {
      state.products = state.products.filter(p => p.id !== action.payload);
    },
    // Очистка ошибок валидации
    clearValidationErrors: (state, action: PayloadAction<string>) => {
      const index = state.products.findIndex(p => p.id === action.payload);
      if (index !== -1) {
        delete state.products[index].validationErrors;
      }
    },
    // Установка ошибок валидации
    setValidationErrors: (state, action: PayloadAction<{ productId: string; errors: NewWaveProduct['validationErrors'] }>) => {
      const index = state.products.findIndex(p => p.id === action.payload.productId);
      if (index !== -1) {
        state.products[index].validationErrors = action.payload.errors;
      }
    },
    // Очистка всех товаров
    clearAllProducts: (state) => {
      state.products = [];
    },
    // Очистка ошибок
    clearError: (state) => {
      state.error = null;
      state.fetchingSkuError = null;
    },
    setNewWavePagination: (state, action: PayloadAction<Partial<NewWavePagination>>) => {
      Object.assign(state.pagination, action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchProductBySku
      .addCase(fetchProductBySku.pending, (state) => {
        state.fetchingSku = true;
        state.fetchingSkuError = null;
      })
      .addCase(fetchProductBySku.fulfilled, (state, action) => {
        state.fetchingSku = false;
        // Преобразуем данные из API в формат NewWaveProduct
        const apiData = action.payload;
        // SKU уже проверен в БД в thunk, но нужно проверить локальный список
        const dbCheckedSku = apiData.id !== null && apiData.id !== undefined ? String(apiData.id) : '';
        if (!dbCheckedSku) {
          console.error('Product ID is missing in API response:', apiData);
          return;
        }
        // Дополнительно проверяем локальный список (на случай, если товар уже добавлен в этой сессии)
        const availableSku = findAvailableSku(dbCheckedSku, state.products);
        // Используем оригинальный SKU для генерации URL (до постфикса)
        const originalSku = (apiData as any)._originalSku || dbCheckedSku;
        const product: NewWaveProduct = {
          id: availableSku,
          name: apiData.name || null,
          subject_id: apiData.subject_id || null,
          url: apiData.url || `https://www.wildberries.ru/catalog/${extractBaseSku(originalSku)}/detail.aspx`,
          thumb_url: apiData.thumb_url || null,
          thumb_middle_url: apiData.thumb_middle_url || null,
          balance: apiData.balance || null,
          comments: apiData.comments || null,
          rating: apiData.rating || null,
          final_price: apiData.final_price || null,
          final_price_max: apiData.final_price_max || null,
          final_price_min: apiData.final_price_min || null,
          final_price_average: apiData.final_price_average || null,
          final_price_median: apiData.final_price_median || null,
          sales: apiData.sales || null,
          sales_per_day_average: apiData.sales_per_day_average || null,
          days_in_stock: apiData.days_in_stock || null,
          days_with_sales: apiData.days_with_sales || null,
          average_if_in_stock: apiData.average_if_in_stock || null,
          commission_fbo: apiData.commission_fbo ?? 0,
          commission_fbs: apiData.commission_fbs ?? null,
          search_words_count: apiData.search_words_count || null,
          search_position_avg: apiData.search_position_avg || null,
          search_visibility: apiData.search_visibility || null,
          warehouses_count: apiData.warehouses_count || null,
          size_count: apiData.size_count || null,
          size_count_in_stock: apiData.size_count_in_stock || null,
          graph: apiData.graph || [],
          stocks_graph: apiData.stocks_graph || [],
          price_graph: apiData.price_graph || [],
          product_visibility_graph: apiData.product_visibility_graph || [],
          category_graph: apiData.category_graph || [],
          category_position_graph: apiData.category_position_graph || [],
          search_position_graph: apiData.search_position_graph || [],
          warehouses_count_graph: apiData.warehouses_count_graph || [],
          size_count_in_stock_graph: apiData.size_count_in_stock_graph || [],
          isSaved: false,
          isSaving: false
        };
        
        // Всегда добавляем новый товар в начало списка, даже если товар с таким ID уже есть
          state.products.unshift(product);
      })
      .addCase(fetchProductBySku.rejected, (state, action) => {
        state.fetchingSku = false;
        state.fetchingSkuError = action.payload as string || 'Failed to fetch product by SKU';
      })
      // fetchNewWaveProducts
      .addCase(fetchNewWaveProducts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchNewWaveProducts.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        const payload = action.payload as { data: any[]; total: number; page: number; limit: number; totalPages: number };
        const products = Array.isArray(payload.data) ? payload.data : [];
        state.pagination.total = payload.total;
        state.pagination.totalPages = payload.totalPages;
        state.products = products.map((apiData: any) => {
          const productId = apiData.id !== null && apiData.id !== undefined ? String(apiData.id) : '';
          if (!productId) {
            console.error('Product ID is missing in API response:', apiData);
            return null;
          }
          return {
            id: productId,
            name: apiData.name || null,
            subject_id: apiData.subject_id || null,
            url: apiData.url || null,
            thumb_url: apiData.thumb_url || null,
            thumb_middle_url: apiData.thumb_middle_url || null,
            balance: apiData.balance || null,
            comments: apiData.comments || null,
            rating: apiData.rating || null,
            final_price: apiData.final_price || null,
            final_price_max: apiData.final_price_max || null,
            final_price_min: apiData.final_price_min || null,
            final_price_average: apiData.final_price_average || null,
            final_price_median: apiData.final_price_median || null,
            sales: apiData.sales || null,
            sales_per_day_average: apiData.sales_per_day_average || null,
            days_in_stock: apiData.days_in_stock || null,
            days_with_sales: apiData.days_with_sales || null,
            average_if_in_stock: apiData.average_if_in_stock || null,
            commission_fbo: apiData.commission_fbo ?? 0,
            commission_fbs: apiData.commission_fbs ?? null,
            search_words_count: apiData.search_words_count || null,
            search_position_avg: apiData.search_position_avg || null,
            search_visibility: apiData.search_visibility || null,
            warehouses_count: apiData.warehouses_count || null,
            size_count: apiData.size_count || null,
            size_count_in_stock: apiData.size_count_in_stock || null,
            graph: apiData.graph || [],
            stocks_graph: apiData.stocks_graph || [],
            price_graph: apiData.price_graph || [],
            product_visibility_graph: apiData.product_visibility_graph || [],
            category_graph: apiData.category_graph || [],
            category_position_graph: apiData.category_position_graph || [],
            search_position_graph: apiData.search_position_graph || [],
            warehouses_count_graph: apiData.warehouses_count_graph || [],
            size_count_in_stock_graph: apiData.size_count_in_stock_graph || [],
            article: apiData.article || null,
            requiredPrice: apiData.requiredPrice || apiData.final_price_average || null,
            certificate: apiData.certificate || null,
            orderQuantity: apiData.orderQuantity || null,
            packageLength: apiData.packageLength || null,
            packageWidth: apiData.packageWidth || null,
            packageHeight: apiData.packageHeight || null,
            purchasePercent: apiData.purchasePercent || 0,
            new_product: apiData.new_product === true,
            target_price_cny: apiData.target_price_cny != null ? apiData.target_price_cny : null,
            isSaved: apiData.isSaved !== undefined ? apiData.isSaved : true,
            isSaving: false,
            validationErrors: undefined
          };
        }).filter((p: NewWaveProduct | null) => p !== null) as NewWaveProduct[];
      })
      .addCase(fetchNewWaveProducts.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch new wave products';
      })
      // saveNewWaveProduct
      .addCase(saveNewWaveProduct.pending, (state, action) => {
        const productId = action.meta.arg.productId;
        const index = state.products.findIndex(p => p.id === productId);
        if (index !== -1) {
          state.products[index].isSaving = true;
        }
      })
      .addCase(saveNewWaveProduct.fulfilled, (state, action) => {
        const productId = action.meta.arg.productId;
        const index = state.products.findIndex(p => p.id === productId);
        if (index !== -1) {
          state.products[index].isSaving = false;
          state.products[index].isSaved = true;
          // Обновляем ID товара на finalId из ответа сервера (может отличаться от отправленного)
          if (action.payload?.data?.id) {
            state.products[index].id = String(action.payload.data.id);
          }
          // Обновляем thumb_url если он был изменен
          if (action.payload?.data?.thumb_url || action.payload?.thumb_url) {
            state.products[index].thumb_url = action.payload.data?.thumb_url || action.payload.thumb_url;
          }
          if (action.payload?.data?.article != null) {
            state.products[index].article = action.payload.data.article;
          }
          // Очищаем ошибки валидации
          delete state.products[index].validationErrors;
        }
      })
      .addCase(saveNewWaveProduct.rejected, (state, action) => {
        const productId = action.meta.arg.productId;
        const index = state.products.findIndex(p => p.id === productId);
        if (index !== -1) {
          state.products[index].isSaving = false;
          
          // Если есть ошибки валидации, сохраняем их
          const error = action.payload as any;
          if (error?.validationErrors) {
            state.products[index].validationErrors = error.validationErrors;
          }
        }
        state.error = (action.payload as any)?.error || 'Failed to save product';
      })
      // confirmNewProduct
      .addCase(confirmNewProduct.fulfilled, (state, action) => {
        const { productId, confirmed, targetPriceCny } = action.payload;
        const index = state.products.findIndex(p => p.id === productId);
        if (index !== -1) {
          state.products[index].new_product = confirmed;
          if (confirmed && targetPriceCny !== undefined) {
            state.products[index].target_price_cny = targetPriceCny;
          }
        }
      });
  }
});

export const { 
  addProduct, 
  updateProduct, 
  removeProduct, 
  clearValidationErrors,
  setValidationErrors,
  clearAllProducts,
  clearError,
  setNewWavePagination
} = newWaveSlice.actions;

export default newWaveSlice.reducer;

